import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useState } from 'react';
import { ChevronDown, Link2, Link2Off, Package, Monitor, CreditCard } from 'lucide-react';
import ShimmerImage from '../../components/ShimmerImage';
import Barcode from '../../components/Barcode';
import type { DeviceUnitRow, DeviceModelRow } from '../../services/hardwareApi';

/**
 * THE SHAPE OF AN ORDER STAGE — nothing else.
 *
 * Every panel on the order rail had grown its own card, its own chip and its
 * own idea of what a device row looks like, so the same four machines were
 * drawn four different ways down one page. Worse, the work for one device was
 * split across the page: its barcode in one panel, its checklist in another,
 * its pair in a third.
 *
 * These are the pieces the stages now share. They are PRESENTATIONAL — no
 * fetching, no saving, no rules. A stage keeps every bit of its own logic and
 * simply hands the result to a `<Disclosure>`; the panel that owns a piece of
 * work is mounted INSIDE that disclosure, unchanged, scoped by the props it
 * already takes (`unitIds`, `posUnitIds`, `stageIds`).
 *
 * That is the whole design: this file moves things, it does not do things. If
 * a behaviour question ever needs answering in here, it belongs in the panel
 * that owns it instead.
 */

/* ── chips ───────────────────────────────────────────────────────────────── */

export type Tone = 'done' | 'work' | 'idle' | 'bad';

const TONE: Record<Tone, string> = {
  done: 'bg-emerald-100 text-emerald-700',
  work: 'bg-amber-100 text-amber-800',
  idle: 'bg-stone-200 text-stone-600',
  bad: 'bg-red-100 text-red-700',
};

export const StageChip: React.FC<{ tone?: Tone; children: React.ReactNode }> = ({ tone = 'idle', children }) => (
  <span className={`shrink-0 whitespace-nowrap rounded px-2 py-0.5 text-[11px] font-semibold ${TONE[tone]}`}>
    {children}
  </span>
);

/**
 * HOW MANY OF THIS MODEL ARE RESERVED — and the three answers it can have.
 *
 * "4 of 4" and "2 of 4" used to read the same way on a collapsed row, so a
 * half-filled model looked finished. Part-allocated is its own tone, and the
 * numbers are always said.
 */
export const AllocationChip: React.FC<{ got: number; want: number }> = ({ got, want }) => {
  if (got >= want && want > 0) return <StageChip tone="done">Allocated · {got} of {want}</StageChip>;
  if (got === 0) return <StageChip tone="idle">Not allocated · 0 of {want}</StageChip>;
  return <StageChip tone="work">Part allocated · {got} of {want}</StageChip>;
};

/* ── the collapsible ─────────────────────────────────────────────────────── */

/**
 * ONE COLLAPSIBLE CARD.
 *
 * Uncontrolled by default (`defaultOpen`), controlled when a caller needs to
 * drive it (`open` + `onToggle`) — the allocate panel opens the model it just
 * filled, which it cannot do from the outside otherwise.
 *
 * The header is a real <button>, so it is reachable by keyboard and announces
 * its state; anything interactive that belongs to the row goes in `actions`,
 * OUTSIDE that button, because a nested button is invalid and swallows clicks.
 */
export const Disclosure: React.FC<{
  header: React.ReactNode;
  chip?: React.ReactNode;
  actions?: React.ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onToggle?: (next: boolean) => void;
  tone?: 'plain' | 'done' | 'bad' | 'dashed';
  headerClassName?: string;
  className?: string;
  children?: React.ReactNode;
}> = ({
  header, chip, actions, defaultOpen = false, open, onToggle,
  tone = 'plain', headerClassName = '', className = '', children,
}) => {
  const [mine, setMine] = useState(defaultOpen);
  const isOpen = open ?? mine;
  const toggle = () => { if (onToggle) onToggle(!isOpen); else setMine((v) => !v); };

  const edge = tone === 'done' ? 'border-emerald-200'
    : tone === 'bad' ? 'border-red-200'
      : tone === 'dashed' ? 'border-dashed border-stone-300'
        : 'border-stone-200';

  return (
    <div className={`overflow-hidden rounded-xl border bg-white ${edge} ${className}`}>
      <div className={`flex items-center gap-2 ${headerClassName}`}>
        <button
          type="button"
          onClick={toggle}
          aria-expanded={isOpen}
          className="flex min-h-[56px] flex-1 items-center gap-3 px-3 py-2.5 text-left hover:bg-stone-50"
        >
          <span className="min-w-0 flex-1">{header}</span>
          {chip}
          <ChevronDown
            size={14}
            className={`shrink-0 text-stone-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>
        {actions && <span className="shrink-0 pr-3">{actions}</span>}
      </div>
      {isOpen && children}
    </div>
  );
};

/** The body every disclosure uses, so stages cannot drift on padding. */
export const DisclosureBody: React.FC<{ className?: string; children: React.ReactNode }> = ({
  className = '', children,
}) => (
  <div className={`border-t border-stone-100 bg-stone-50 p-3 ${className}`}>{children}</div>
);

/* ── device identity ─────────────────────────────────────────────────────── */

/** What the box is called. Same fallback order the rest of the rail uses. */
export const unitLabel = (u: { trackingId?: string; serialNumber?: string; unitId: string }): string =>
  u.trackingId || u.serialNumber || u.unitId;

export const DeviceThumb: React.FC<{
  model?: DeviceModelRow;
  size?: number;
  kind?: 'pos' | 'payment' | 'other';
}> = ({ model, size = 40, kind = 'other' }) => {
  const Icon = kind === 'pos' ? Monitor : kind === 'payment' ? CreditCard : Package;
  return (
    <ShimmerImage
      src={model?.media?.heroImageUrl}
      alt={model?.modelName || 'device'}
      width={size}
      height={size}
      objectFit="contain"
      className="shrink-0 rounded-lg border border-stone-200"
      fallbackBgColor="bg-stone-100"
      fallbackIcon={<Icon size={Math.round(size * 0.4)} className="text-stone-400" />}
      variant="light"
    />
  );
};

/**
 * THE BARCODE AND ITS NUMBER — the same symbol the Rollo label carries.
 *
 * Drawn from the shared `Barcode`, which is the shared `code128Geometry`, so a
 * symbol on this screen and a symbol on the box can never disagree.
 */
export const UnitCode: React.FC<{ value: string; height?: number; width?: number }> = ({
  value, height = 20, width = 1.1,
}) => (
  <span className="flex shrink-0 flex-col items-center gap-[3px]">
    <Barcode value={value} height={height} moduleWidth={width} color="#1C1917" />
    <span className="whitespace-nowrap font-mono text-[10px] font-semibold tracking-[0.08em] text-stone-800">
      {value}
    </span>
  </span>
);

/**
 * ONE DEVICE, AS A ROW HEADER — picture, barcode, serial, where it is.
 *
 * `state` is said in words rather than left as a colour: "Allocated ·
 * Provisioning" are two different facts about the same box and an operator
 * reads both.
 */
export const UnitHeader: React.FC<{
  unit: DeviceUnitRow;
  model?: DeviceModelRow;
  kind?: 'pos' | 'payment' | 'other';
  /** A line under the serial — where it is, or what is holding it. */
  note?: React.ReactNode;
}> = ({ unit, model, kind, note }) => (
  <span className="flex min-w-0 items-center gap-3">
    <DeviceThumb model={model} size={38} kind={kind} />
    <UnitCode value={unitLabel(unit)} width={0.95} />
    <span className="min-w-0 flex-1">
      {unit.serialNumber && (
        <span className="block break-all font-mono text-[11.5px] leading-tight text-stone-600">
          <span className="mr-1.5 font-sans text-[9.5px] font-bold uppercase tracking-wide text-stone-400">
            Serial
          </span>
          {unit.serialNumber}
        </span>
      )}
      <span className="mt-0.5 block text-[11px] text-stone-500">
        {note ?? <span className="capitalize">{String(unit.state || '').replace(/_/g, ' ')}</span>}
      </span>
    </span>
  </span>
);

/**
 * A PAIR, AS A ROW HEADER.
 *
 * After configuration the unit of work stops being a machine and becomes a
 * couple — a test sale needs both halves, and so does a box. `payment` absent
 * is drawn as a broken link rather than as an empty space, because "not
 * coupled yet" is a state somebody has to act on.
 */
export const CoupleHeader: React.FC<{
  pos: DeviceUnitRow;
  posModel?: DeviceModelRow;
  payment?: DeviceUnitRow;
  paymentModel?: DeviceModelRow;
  /** Drawn in place of the reader when there is none — the picker, usually. */
  fallback?: React.ReactNode;
}> = ({ pos, posModel, payment, paymentModel, fallback }) => (
  <span className="flex min-w-0 flex-wrap items-center gap-3">
    <span className="flex min-w-0 items-center gap-2.5">
      <DeviceThumb model={posModel} size={38} kind="pos" />
      <span className="min-w-0">
        <span className="block truncate text-[12px] font-semibold text-stone-800">
          {posModel?.modelName || pos.modelId}
        </span>
        <span className="block font-mono text-[10px] font-semibold tracking-[0.07em] text-stone-500">
          {unitLabel(pos)}
        </span>
        <Barcode value={unitLabel(pos)} height={16} moduleWidth={0.9} color="#1C1917" />
      </span>
    </span>

    <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full ${
      payment ? 'bg-emerald-50 text-emerald-600' : 'bg-stone-100 text-stone-400'}`}
    >
      {payment ? <Link2 size={14} /> : <Link2Off size={14} />}
    </span>

    {payment ? (
      <span className="flex min-w-0 items-center gap-2.5">
        <DeviceThumb model={paymentModel} size={38} kind="payment" />
        <span className="min-w-0">
          <span className="block truncate text-[12px] font-semibold text-stone-800">
            {paymentModel?.modelName || payment.modelId}
            {payment.unitId === pos.unitId && (
              <span className="ml-1 text-[10.5px] font-normal text-stone-400">· all-in-one</span>
            )}
          </span>
          <span className="block font-mono text-[10px] font-semibold tracking-[0.07em] text-stone-500">
            {unitLabel(payment)}
          </span>
          <Barcode value={unitLabel(payment)} height={16} moduleWidth={0.9} color="#1C1917" />
        </span>
      </span>
    ) : fallback}
  </span>
);

/**
 * A STEP INSIDE A CARD — collapsed to one line, opening onto the panel that
 * owns the work.
 *
 * The runner used to print every step of every stage flat down the page, which
 * on a set-up till is several screens of detail nobody is reading yet. A step
 * says what it is and how it went; opening it is how you get the rest.
 */
export type StepState = 'pending' | 'running' | 'done' | 'failed' | 'skipped';

export const StepRow: React.FC<{
  label: string;
  summary?: React.ReactNode;
  state: StepState;
  defaultOpen?: boolean;
  children?: React.ReactNode;
}> = ({ label, summary, state, defaultOpen, children }) => {
  const edge = state === 'done' ? 'border-emerald-200'
    : state === 'failed' ? 'border-red-200'
      : state === 'running' ? 'border-[#DC143C]'
        : 'border-stone-200';

  const mark = state === 'done' ? 'bg-emerald-500 text-white'
    : state === 'failed' ? 'bg-red-600 text-white'
      : state === 'running' ? ''
        : 'border border-stone-300 bg-white';

  return (
    <Disclosure
      className={`${edge} ${state === 'pending' ? 'opacity-70' : ''}`}
      defaultOpen={defaultOpen}
      header={(
        <span className="flex min-w-0 items-center gap-2.5">
          <span className={`grid h-[19px] w-[19px] shrink-0 place-items-center rounded-full text-[10px] font-bold ${mark}`}>
            {state === 'done' ? '✓' : state === 'failed' ? '✕' : state === 'running' ? <RubyGemMark size={16} drawing /> : ''}
          </span>
          <span className="min-w-0 flex-1">
            <span className={`block text-[12.5px] font-semibold ${
              state === 'done' ? 'text-emerald-700' : state === 'failed' ? 'text-red-700' : 'text-stone-700'}`}
            >
              {label}
            </span>
            {summary && (
              <span className={`mt-0.5 block text-[11px] leading-snug ${
                state === 'failed' ? 'text-red-600' : 'text-stone-500'}`}
              >
                {summary}
              </span>
            )}
          </span>
        </span>
      )}
    >
      <DisclosureBody>{children}</DisclosureBody>
    </Disclosure>
  );
};

/** An unfilled place in a set, so "2 of 4" looks like two gaps and not like two. */
export const EmptySlot: React.FC<{
  model?: DeviceModelRow;
  text: string;
  action?: React.ReactNode;
}> = ({ model, text, action }) => (
  <div className="flex min-h-[56px] items-center gap-3 rounded-xl border border-dashed border-stone-300 bg-stone-50 px-3 py-2">
    <span className="opacity-60"><DeviceThumb model={model} size={34} /></span>
    <span className="min-w-0 flex-1 text-[12px] text-stone-500">{text}</span>
    {action}
  </div>
);
